const theme = {
  colors: {
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
  },
  container: {
    center: true,
    padding: {
      DEFAULT: "1rem",
      sm: "2rem",
      md: "3rem",
      lg: "4rem",
      xl: "5rem",
      "2xl": "6rem",
    },
  },
  fontFamily: {
    sans: [
      "raleway",
      "ui-sans-serif",
      "system-ui",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      '"Noto Sans"',
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
      '"Noto Color Emoji"',
    ],
    serif: [
      "raleway",
      "ui-serif",
      "Georgia",
      "Cambria",
      '"Times New Roman"',
      "Times",
      "serif",
    ],
  },
  fontSize: {
    xs: ["0.6rem", "1rem"],
    sm: [".875rem", "1.25rem"],
    base: ["1rem", "1.5rem"],
    lg: ["1.125rem", "1.75rem"],
    xl: ["1.25rem", "1.75rem"],
    headingSm: ["1.5rem", "2rem"],
    headingBase: ["2rem", "2.5rem"],
    headingLg: ["2.5rem", "3rem"],
    headingXl: ["3rem", "3.5rem"],
  },
  fontWeight: { light: 300, normal: 400, bold: 600 },
  listStyleType: {
    none: "none",
    disc: "disc",
    decimal: "decimal",
    leaf: "leaf",
  },
  screens: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
    "2xlmax": { max: "1535px" },
    xlmax: { max: "1279px" },
    lgmax: { max: "1023px" },
    mdmax: { max: "767px" },
    smmax: { max: "639px" },
  },
  spacing: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
  },
  typography: {
    DEFAULT: {
      css: [
        {
          color: "#374151",
          maxWidth: "65ch",
          '[class~="lead"]': { color: "#4b5563" },
          a: {
            color: "#111827",
            textDecoration: "underline",
            fontWeight: "500",
          },
          strong: { color: "#111827", fontWeight: "600" },
          'ol[type="A"]': { "--list-counter-style": "upper-alpha" },
          'ol[type="a"]': { "--list-counter-style": "lower-alpha" },
          'ol[type="A s"]': { "--list-counter-style": "upper-alpha" },
          'ol[type="a s"]': { "--list-counter-style": "lower-alpha" },
          'ol[type="I"]': { "--list-counter-style": "upper-roman" },
          'ol[type="i"]': { "--list-counter-style": "lower-roman" },
          'ol[type="I s"]': { "--list-counter-style": "upper-roman" },
          'ol[type="i s"]': { "--list-counter-style": "lower-roman" },
          'ol[type="1"]': { "--list-counter-style": "decimal" },
          "ol > li": { position: "relative" },
          "ol > li::before": {
            content:
              'counter(list-item, var(--list-counter-style, decimal)) "."',
            position: "absolute",
            fontWeight: "400",
            color: "#6b7280",
          },
          "ul > li": { position: "relative" },
          "ul > li::before": {
            content: '""',
            position: "absolute",
            backgroundColor: "#d1d5db",
            borderRadius: "50%",
          },
          hr: { borderColor: "#e5e7eb", borderTopWidth: 1 },
          blockquote: {
            fontWeight: "500",
            fontStyle: "italic",
            color: "#111827",
            borderLeftWidth: "0.25rem",
            borderLeftColor: "#e5e7eb",
            quotes: '"\\201C""\\201D""\\2018""\\2019"',
          },
          "blockquote p:first-of-type::before": { content: "open-quote" },
          "blockquote p:last-of-type::after": { content: "close-quote" },
          h1: { color: "#111827", fontWeight: "800" },
          h2: { color: "#111827", fontWeight: "700" },
          h3: { color: "#111827", fontWeight: "600" },
          h4: { color: "#111827", fontWeight: "600" },
          "figure figcaption": { color: "#6b7280" },
          code: { color: "#111827", fontWeight: "600" },
          "code::before": { content: '"`"' },
          "code::after": { content: '"`"' },
          "a code": { color: "#111827" },
          pre: {
            color: "#e5e7eb",
            backgroundColor: "#1f2937",
            overflowX: "auto",
          },
          "pre code": {
            backgroundColor: "transparent",
            borderWidth: "0",
            borderRadius: "0",
            padding: "0",
            fontWeight: "400",
            color: "inherit",
            fontSize: "inherit",
            fontFamily: "inherit",
            lineHeight: "inherit",
          },
          "pre code::before": { content: "none" },
          "pre code::after": { content: "none" },
          table: {
            width: "100%",
            tableLayout: "auto",
            textAlign: "left",
            marginTop: "2em",
            marginBottom: "2em",
          },
          thead: {
            color: "#111827",
            fontWeight: "600",
            borderBottomWidth: "1px",
            borderBottomColor: "#d1d5db",
          },
          "thead th": { verticalAlign: "bottom" },
          "tbody tr": {
            borderBottomWidth: "1px",
            borderBottomColor: "#e5e7eb",
          },
          "tbody tr:last-child": { borderBottomWidth: "0" },
          "tbody td": { verticalAlign: "top" },
        },
        {
          fontSize: "1rem",
          lineHeight: "1.75",
          p: { marginTop: "1.25em", marginBottom: "1.25em" },
          '[class~="lead"]': {
            fontSize: "1.25em",
            lineHeight: "1.6",
            marginTop: "1.2em",
            marginBottom: "1.2em",
          },
          blockquote: {
            marginTop: "1.6em",
            marginBottom: "1.6em",
            paddingLeft: "1em",
          },
          h1: {
            fontSize: "2.25em",
            marginTop: "0",
            marginBottom: "0.8888889em",
            lineHeight: "1.1111111",
          },
          h2: {
            fontSize: "1.5em",
            marginTop: "2em",
            marginBottom: "1em",
            lineHeight: "1.3333333",
          },
          h3: {
            fontSize: "1.25em",
            marginTop: "1.6em",
            marginBottom: "0.6em",
            lineHeight: "1.6",
          },
          h4: { marginTop: "1.5em", marginBottom: "0.5em", lineHeight: "1.5" },
          img: { marginTop: "2em", marginBottom: "2em" },
          video: { marginTop: "2em", marginBottom: "2em" },
          figure: { marginTop: "2em", marginBottom: "2em" },
          "figure > *": { marginTop: "0", marginBottom: "0" },
          "figure figcaption": {
            fontSize: "0.875em",
            lineHeight: "1.4285714",
            marginTop: "0.8571429em",
          },
          code: { fontSize: "0.875em" },
          "h2 code": { fontSize: "0.875em" },
          "h3 code": { fontSize: "0.9em" },
          pre: {
            fontSize: "0.875em",
            lineHeight: "1.7142857",
            marginTop: "1.7142857em",
            marginBottom: "1.7142857em",
            borderRadius: "0.375rem",
            paddingTop: "0.8571429em",
            paddingRight: "1.1428571em",
            paddingBottom: "0.8571429em",
            paddingLeft: "1.1428571em",
          },
          ol: { marginTop: "1.25em", marginBottom: "1.25em" },
          ul: { marginTop: "1.25em", marginBottom: "1.25em" },
          li: { marginTop: "0.5em", marginBottom: "0.5em" },
          "ol > li": { paddingLeft: "1.75em" },
          "ol > li::before": { left: "0" },
          "ul > li": { paddingLeft: "1.75em" },
          "ul > li::before": {
            width: "0.375em",
            height: "0.375em",
            top: "calc(0.875em - 0.1875em)",
            left: "0.25em",
          },
          "> ul > li p": { marginTop: "0.75em", marginBottom: "0.75em" },
          "> ul > li > *:first-child": { marginTop: "1.25em" },
          "> ul > li > *:last-child": { marginBottom: "1.25em" },
          "> ol > li > *:first-child": { marginTop: "1.25em" },
          "> ol > li > *:last-child": { marginBottom: "1.25em" },
          "ul ul, ul ol, ol ul, ol ol": {
            marginTop: "0.75em",
            marginBottom: "0.75em",
          },
          hr: { marginTop: "3em", marginBottom: "3em" },
          "hr + *": { marginTop: "0" },
          "h2 + *": { marginTop: "0" },
          "h3 + *": { marginTop: "0" },
          "h4 + *": { marginTop: "0" },
          table: { fontSize: "0.875em", lineHeight: "1.7142857" },
          "thead th": {
            paddingRight: "0.5714286em",
            paddingBottom: "0.5714286em",
            paddingLeft: "0.5714286em",
          },
          "thead th:first-child": { paddingLeft: "0" },
          "thead th:last-child": { paddingRight: "0" },
          "tbody td": {
            paddingTop: "0.5714286em",
            paddingRight: "0.5714286em",
            paddingBottom: "0.5714286em",
            paddingLeft: "0.5714286em",
          },
          "tbody td:first-child": { paddingLeft: "0" },
          "tbody td:last-child": { paddingRight: "0" },
        },
        {
          "> :first-child": { marginTop: "0" },
          "> :last-child": { marginBottom: "0" },
        },
        {
          color: "#4b5563",
          maxWidth: "100%",
          fontWeight: 300,
          a: {
            color: "#4c5e0d",
            fontWeight: 600,
            "&.no-link-style": {
              color: "inherit",
              textDecoration: "inherit",
              "&:hover": { color: "inherit" },
            },
            "&:hover": { color: "#d2d756" },
          },
          "h1, h2, h3, h4, h5, h6": {
            color: "#4c5e0d",
            fontFamily:
              'raleway, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
            fontWeight: 600,
            marginTop: 0,
            marginBottom: "1.2rem",
            "& span": { color: "#212121", fontWeight: 400 },
          },
          h1: { fontSize: "3rem", lineHeight: "3.5rem" },
          h2: { fontSize: "2.5rem", lineHeight: "3rem" },
          h3: { fontSize: "2rem", lineHeight: "2.5rem" },
          "h4, h5, h6": { fontSize: "1.5rem", lineHeight: "2rem" },
          img: { "margin-top": 0, "margin-bottom": 0 },
        },
      ],
    },
    sm: {
      css: [
        {
          fontSize: "0.875rem",
          lineHeight: "1.7142857",
          p: { marginTop: "1.1428571em", marginBottom: "1.1428571em" },
          '[class~="lead"]': {
            fontSize: "1.2857143em",
            lineHeight: "1.5555556",
            marginTop: "0.8888889em",
            marginBottom: "0.8888889em",
          },
          blockquote: {
            marginTop: "1.3333333em",
            marginBottom: "1.3333333em",
            paddingLeft: "1.1111111em",
          },
          h1: {
            fontSize: "2.1428571em",
            marginTop: "0",
            marginBottom: "0.8em",
            lineHeight: "1.2",
          },
          h2: {
            fontSize: "1.4285714em",
            marginTop: "1.6em",
            marginBottom: "0.8em",
            lineHeight: "1.4",
          },
          h3: {
            fontSize: "1.2857143em",
            marginTop: "1.5555556em",
            marginBottom: "0.4444444em",
            lineHeight: "1.5555556",
          },
          h4: {
            marginTop: "1.4285714em",
            marginBottom: "0.5714286em",
            lineHeight: "1.4285714",
          },
          img: { marginTop: "1.7142857em", marginBottom: "1.7142857em" },
          video: { marginTop: "1.7142857em", marginBottom: "1.7142857em" },
          figure: { marginTop: "1.7142857em", marginBottom: "1.7142857em" },
          "figure > *": { marginTop: "0", marginBottom: "0" },
          "figure figcaption": {
            fontSize: "0.8571429em",
            lineHeight: "1.3333333",
            marginTop: "0.6666667em",
          },
          code: { fontSize: "0.8571429em" },
          "h2 code": { fontSize: "0.9em" },
          "h3 code": { fontSize: "0.8888889em" },
          pre: {
            fontSize: "0.8571429em",
            lineHeight: "1.6666667",
            marginTop: "1.6666667em",
            marginBottom: "1.6666667em",
            borderRadius: "0.25rem",
            paddingTop: "0.6666667em",
            paddingRight: "1em",
            paddingBottom: "0.6666667em",
            paddingLeft: "1em",
          },
          ol: { marginTop: "1.1428571em", marginBottom: "1.1428571em" },
          ul: { marginTop: "1.1428571em", marginBottom: "1.1428571em" },
          li: { marginTop: "0.2857143em", marginBottom: "0.2857143em" },
          "ol > li": { paddingLeft: "1.5714286em" },
          "ol > li::before": { left: "0" },
          "ul > li": { paddingLeft: "1.5714286em" },
          "ul > li::before": {
            height: "0.3571429em",
            width: "0.3571429em",
            top: "calc(0.8571429em - 0.1785714em)",
            left: "0.2142857em",
          },
          "> ul > li p": {
            marginTop: "0.5714286em",
            marginBottom: "0.5714286em",
          },
          "> ul > li > *:first-child": { marginTop: "1.1428571em" },
          "> ul > li > *:last-child": { marginBottom: "1.1428571em" },
          "> ol > li > *:first-child": { marginTop: "1.1428571em" },
          "> ol > li > *:last-child": { marginBottom: "1.1428571em" },
          "ul ul, ul ol, ol ul, ol ol": {
            marginTop: "0.5714286em",
            marginBottom: "0.5714286em",
          },
          hr: { marginTop: "2.8571429em", marginBottom: "2.8571429em" },
          "hr + *": { marginTop: "0" },
          "h2 + *": { marginTop: "0" },
          "h3 + *": { marginTop: "0" },
          "h4 + *": { marginTop: "0" },
          table: { fontSize: "0.8571429em", lineHeight: "1.5" },
          "thead th": {
            paddingRight: "1em",
            paddingBottom: "0.6666667em",
            paddingLeft: "1em",
          },
          "thead th:first-child": { paddingLeft: "0" },
          "thead th:last-child": { paddingRight: "0" },
          "tbody td": {
            paddingTop: "0.6666667em",
            paddingRight: "1em",
            paddingBottom: "0.6666667em",
            paddingLeft: "1em",
          },
          "tbody td:first-child": { paddingLeft: "0" },
          "tbody td:last-child": { paddingRight: "0" },
        },
        {
          "> :first-child": { marginTop: "0" },
          "> :last-child": { marginBottom: "0" },
        },
      ],
    },
    lg: {
      css: [
        {
          fontSize: "1.125rem",
          lineHeight: "1.7777778",
          p: { marginTop: "1.3333333em", marginBottom: "1.3333333em" },
          '[class~="lead"]': {
            fontSize: "1.2222222em",
            lineHeight: "1.4545455",
            marginTop: "1.0909091em",
            marginBottom: "1.0909091em",
          },
          blockquote: {
            marginTop: "1.6666667em",
            marginBottom: "1.6666667em",
            paddingLeft: "1em",
          },
          h1: {
            fontSize: "2.6666667em",
            marginTop: "0",
            marginBottom: "0.8333333em",
            lineHeight: "1",
          },
          h2: {
            fontSize: "1.6666667em",
            marginTop: "1.8666667em",
            marginBottom: "1.0666667em",
            lineHeight: "1.3333333",
          },
          h3: {
            fontSize: "1.3333333em",
            marginTop: "1.6666667em",
            marginBottom: "0.6666667em",
            lineHeight: "1.5",
          },
          h4: {
            marginTop: "1.7777778em",
            marginBottom: "0.4444444em",
            lineHeight: "1.5555556",
          },
          img: { marginTop: "1.7777778em", marginBottom: "1.7777778em" },
          video: { marginTop: "1.7777778em", marginBottom: "1.7777778em" },
          figure: { marginTop: "1.7777778em", marginBottom: "1.7777778em" },
          "figure > *": { marginTop: "0", marginBottom: "0" },
          "figure figcaption": {
            fontSize: "0.8888889em",
            lineHeight: "1.5",
            marginTop: "1em",
          },
          code: { fontSize: "0.8888889em" },
          "h2 code": { fontSize: "0.8666667em" },
          "h3 code": { fontSize: "0.875em" },
          pre: {
            fontSize: "0.8888889em",
            lineHeight: "1.75",
            marginTop: "2em",
            marginBottom: "2em",
            borderRadius: "0.375rem",
            paddingTop: "1em",
            paddingRight: "1.5em",
            paddingBottom: "1em",
            paddingLeft: "1.5em",
          },
          ol: { marginTop: "1.3333333em", marginBottom: "1.3333333em" },
          ul: { marginTop: "1.3333333em", marginBottom: "1.3333333em" },
          li: { marginTop: "0.6666667em", marginBottom: "0.6666667em" },
          "ol > li": { paddingLeft: "1.6666667em" },
          "ol > li::before": { left: "0" },
          "ul > li": { paddingLeft: "1.6666667em" },
          "ul > li::before": {
            width: "0.3333333em",
            height: "0.3333333em",
            top: "calc(0.8888889em - 0.1666667em)",
            left: "0.2222222em",
          },
          "> ul > li p": {
            marginTop: "0.8888889em",
            marginBottom: "0.8888889em",
          },
          "> ul > li > *:first-child": { marginTop: "1.3333333em" },
          "> ul > li > *:last-child": { marginBottom: "1.3333333em" },
          "> ol > li > *:first-child": { marginTop: "1.3333333em" },
          "> ol > li > *:last-child": { marginBottom: "1.3333333em" },
          "ul ul, ul ol, ol ul, ol ol": {
            marginTop: "0.8888889em",
            marginBottom: "0.8888889em",
          },
          hr: { marginTop: "3.1111111em", marginBottom: "3.1111111em" },
          "hr + *": { marginTop: "0" },
          "h2 + *": { marginTop: "0" },
          "h3 + *": { marginTop: "0" },
          "h4 + *": { marginTop: "0" },
          table: { fontSize: "0.8888889em", lineHeight: "1.5" },
          "thead th": {
            paddingRight: "0.75em",
            paddingBottom: "0.75em",
            paddingLeft: "0.75em",
          },
          "thead th:first-child": { paddingLeft: "0" },
          "thead th:last-child": { paddingRight: "0" },
          "tbody td": {
            paddingTop: "0.75em",
            paddingRight: "0.75em",
            paddingBottom: "0.75em",
            paddingLeft: "0.75em",
          },
          "tbody td:first-child": { paddingLeft: "0" },
          "tbody td:last-child": { paddingRight: "0" },
        },
        {
          "> :first-child": { marginTop: "0" },
          "> :last-child": { marginBottom: "0" },
        },
      ],
    },
    xl: {
      css: [
        {
          fontSize: "1.25rem",
          lineHeight: "1.8",
          p: { marginTop: "1.2em", marginBottom: "1.2em" },
          '[class~="lead"]': {
            fontSize: "1.2em",
            lineHeight: "1.5",
            marginTop: "1em",
            marginBottom: "1em",
          },
          blockquote: {
            marginTop: "1.6em",
            marginBottom: "1.6em",
            paddingLeft: "1.0666667em",
          },
          h1: {
            fontSize: "2.8em",
            marginTop: "0",
            marginBottom: "0.8571429em",
            lineHeight: "1",
          },
          h2: {
            fontSize: "1.8em",
            marginTop: "1.5555556em",
            marginBottom: "0.8888889em",
            lineHeight: "1.1111111",
          },
          h3: {
            fontSize: "1.5em",
            marginTop: "1.6em",
            marginBottom: "0.6666667em",
            lineHeight: "1.3333333",
          },
          h4: { marginTop: "1.8em", marginBottom: "0.6em", lineHeight: "1.6" },
          img: { marginTop: "2em", marginBottom: "2em" },
          video: { marginTop: "2em", marginBottom: "2em" },
          figure: { marginTop: "2em", marginBottom: "2em" },
          "figure > *": { marginTop: "0", marginBottom: "0" },
          "figure figcaption": {
            fontSize: "0.9em",
            lineHeight: "1.5555556",
            marginTop: "1em",
          },
          code: { fontSize: "0.9em" },
          "h2 code": { fontSize: "0.8611111em" },
          "h3 code": { fontSize: "0.9em" },
          pre: {
            fontSize: "0.9em",
            lineHeight: "1.7777778",
            marginTop: "2em",
            marginBottom: "2em",
            borderRadius: "0.5rem",
            paddingTop: "1.1111111em",
            paddingRight: "1.3333333em",
            paddingBottom: "1.1111111em",
            paddingLeft: "1.3333333em",
          },
          ol: { marginTop: "1.2em", marginBottom: "1.2em" },
          ul: { marginTop: "1.2em", marginBottom: "1.2em" },
          li: { marginTop: "0.6em", marginBottom: "0.6em" },
          "ol > li": { paddingLeft: "1.8em" },
          "ol > li::before": { left: "0" },
          "ul > li": { paddingLeft: "1.8em" },
          "ul > li::before": {
            width: "0.35em",
            height: "0.35em",
            top: "calc(0.9em - 0.175em)",
            left: "0.25em",
          },
          "> ul > li p": { marginTop: "0.8em", marginBottom: "0.8em" },
          "> ul > li > *:first-child": { marginTop: "1.2em" },
          "> ul > li > *:last-child": { marginBottom: "1.2em" },
          "> ol > li > *:first-child": { marginTop: "1.2em" },
          "> ol > li > *:last-child": { marginBottom: "1.2em" },
          "ul ul, ul ol, ol ul, ol ol": {
            marginTop: "0.8em",
            marginBottom: "0.8em",
          },
          hr: { marginTop: "2.8em", marginBottom: "2.8em" },
          "hr + *": { marginTop: "0" },
          "h2 + *": { marginTop: "0" },
          "h3 + *": { marginTop: "0" },
          "h4 + *": { marginTop: "0" },
          table: { fontSize: "0.9em", lineHeight: "1.5555556" },
          "thead th": {
            paddingRight: "0.6666667em",
            paddingBottom: "0.8888889em",
            paddingLeft: "0.6666667em",
          },
          "thead th:first-child": { paddingLeft: "0" },
          "thead th:last-child": { paddingRight: "0" },
          "tbody td": {
            paddingTop: "0.8888889em",
            paddingRight: "0.6666667em",
            paddingBottom: "0.8888889em",
            paddingLeft: "0.6666667em",
          },
          "tbody td:first-child": { paddingLeft: "0" },
          "tbody td:last-child": { paddingRight: "0" },
        },
        {
          "> :first-child": { marginTop: "0" },
          "> :last-child": { marginBottom: "0" },
        },
      ],
    },
    "2xl": {
      css: [
        {
          fontSize: "1.5rem",
          lineHeight: "1.6666667",
          p: { marginTop: "1.3333333em", marginBottom: "1.3333333em" },
          '[class~="lead"]': {
            fontSize: "1.25em",
            lineHeight: "1.4666667",
            marginTop: "1.0666667em",
            marginBottom: "1.0666667em",
          },
          blockquote: {
            marginTop: "1.7777778em",
            marginBottom: "1.7777778em",
            paddingLeft: "1.1111111em",
          },
          h1: {
            fontSize: "2.6666667em",
            marginTop: "0",
            marginBottom: "0.875em",
            lineHeight: "1",
          },
          h2: {
            fontSize: "2em",
            marginTop: "1.5em",
            marginBottom: "0.8333333em",
            lineHeight: "1.0833333",
          },
          h3: {
            fontSize: "1.5em",
            marginTop: "1.5555556em",
            marginBottom: "0.6666667em",
            lineHeight: "1.2222222",
          },
          h4: {
            marginTop: "1.6666667em",
            marginBottom: "0.6666667em",
            lineHeight: "1.5",
          },
          img: { marginTop: "2em", marginBottom: "2em" },
          video: { marginTop: "2em", marginBottom: "2em" },
          figure: { marginTop: "2em", marginBottom: "2em" },
          "figure > *": { marginTop: "0", marginBottom: "0" },
          "figure figcaption": {
            fontSize: "0.8333333em",
            lineHeight: "1.6",
            marginTop: "1em",
          },
          code: { fontSize: "0.8333333em" },
          "h2 code": { fontSize: "0.875em" },
          "h3 code": { fontSize: "0.8888889em" },
          pre: {
            fontSize: "0.8333333em",
            lineHeight: "1.8",
            marginTop: "2em",
            marginBottom: "2em",
            borderRadius: "0.5rem",
            paddingTop: "1.2em",
            paddingRight: "1.6em",
            paddingBottom: "1.2em",
            paddingLeft: "1.6em",
          },
          ol: { marginTop: "1.3333333em", marginBottom: "1.3333333em" },
          ul: { marginTop: "1.3333333em", marginBottom: "1.3333333em" },
          li: { marginTop: "0.5em", marginBottom: "0.5em" },
          "ol > li": { paddingLeft: "1.6666667em" },
          "ol > li::before": { left: "0" },
          "ul > li": { paddingLeft: "1.6666667em" },
          "ul > li::before": {
            width: "0.3333333em",
            height: "0.3333333em",
            top: "calc(0.8333333em - 0.1666667em)",
            left: "0.25em",
          },
          "> ul > li p": {
            marginTop: "0.8333333em",
            marginBottom: "0.8333333em",
          },
          "> ul > li > *:first-child": { marginTop: "1.3333333em" },
          "> ul > li > *:last-child": { marginBottom: "1.3333333em" },
          "> ol > li > *:first-child": { marginTop: "1.3333333em" },
          "> ol > li > *:last-child": { marginBottom: "1.3333333em" },
          "ul ul, ul ol, ol ul, ol ol": {
            marginTop: "0.6666667em",
            marginBottom: "0.6666667em",
          },
          hr: { marginTop: "3em", marginBottom: "3em" },
          "hr + *": { marginTop: "0" },
          "h2 + *": { marginTop: "0" },
          "h3 + *": { marginTop: "0" },
          "h4 + *": { marginTop: "0" },
          table: { fontSize: "0.8333333em", lineHeight: "1.4" },
          "thead th": {
            paddingRight: "0.6em",
            paddingBottom: "0.8em",
            paddingLeft: "0.6em",
          },
          "thead th:first-child": { paddingLeft: "0" },
          "thead th:last-child": { paddingRight: "0" },
          "tbody td": {
            paddingTop: "0.8em",
            paddingRight: "0.6em",
            paddingBottom: "0.8em",
            paddingLeft: "0.6em",
          },
          "tbody td:first-child": { paddingLeft: "0" },
          "tbody td:last-child": { paddingRight: "0" },
        },
        {
          "> :first-child": { marginTop: "0" },
          "> :last-child": { marginBottom: "0" },
        },
      ],
    },
  },
  animation: {
    none: "none",
    spin: "spin 1s linear infinite",
    ping: "ping 1s cubic-bezier(0, 0, 0.2, 1) infinite",
    pulse: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
    bounce: "bounce 1s infinite",
  },
  backgroundColor: {
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
  },
  backgroundImage: {
    none: "none",
    "gradient-to-t": "linear-gradient(to top, var(--tw-gradient-stops))",
    "gradient-to-tr": "linear-gradient(to top right, var(--tw-gradient-stops))",
    "gradient-to-r": "linear-gradient(to right, var(--tw-gradient-stops))",
    "gradient-to-br":
      "linear-gradient(to bottom right, var(--tw-gradient-stops))",
    "gradient-to-b": "linear-gradient(to bottom, var(--tw-gradient-stops))",
    "gradient-to-bl":
      "linear-gradient(to bottom left, var(--tw-gradient-stops))",
    "gradient-to-l": "linear-gradient(to left, var(--tw-gradient-stops))",
    "gradient-to-tl": "linear-gradient(to top left, var(--tw-gradient-stops))",
  },
  backgroundOpacity: {
    0: "0",
    5: "0.05",
    10: "0.1",
    20: "0.2",
    25: "0.25",
    30: "0.3",
    40: "0.4",
    50: "0.5",
    60: "0.6",
    70: "0.7",
    75: "0.75",
    80: "0.8",
    90: "0.9",
    95: "0.95",
    100: "1",
  },
  backgroundPosition: {
    bottom: "bottom",
    center: "center",
    left: "left",
    "left-bottom": "left bottom",
    "left-top": "left top",
    right: "right",
    "right-bottom": "right bottom",
    "right-top": "right top",
    top: "top",
  },
  backgroundSize: { auto: "auto", cover: "cover", contain: "contain" },
  borderColor: {
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
    DEFAULT: "#e5e7eb",
  },
  borderOpacity: {
    0: "0",
    5: "0.05",
    10: "0.1",
    20: "0.2",
    25: "0.25",
    30: "0.3",
    40: "0.4",
    50: "0.5",
    60: "0.6",
    70: "0.7",
    75: "0.75",
    80: "0.8",
    90: "0.9",
    95: "0.95",
    100: "1",
  },
  borderRadius: {
    none: "0px",
    sm: "0.125rem",
    DEFAULT: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
  },
  borderWidth: { 0: "0px", 2: "2px", 4: "4px", 8: "8px", DEFAULT: "1px" },
  boxShadow: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    DEFAULT: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
    none: "none",
  },
  cursor: {
    auto: "auto",
    default: "default",
    pointer: "pointer",
    wait: "wait",
    text: "text",
    move: "move",
    help: "help",
    "not-allowed": "not-allowed",
  },
  divideColor: {
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
    DEFAULT: "#e5e7eb",
  },
  divideOpacity: {
    0: "0",
    5: "0.05",
    10: "0.1",
    20: "0.2",
    25: "0.25",
    30: "0.3",
    40: "0.4",
    50: "0.5",
    60: "0.6",
    70: "0.7",
    75: "0.75",
    80: "0.8",
    90: "0.9",
    95: "0.95",
    100: "1",
  },
  divideWidth: { 0: "0px", 2: "2px", 4: "4px", 8: "8px", DEFAULT: "1px" },
  fill: { current: "currentColor" },
  flex: { 1: "1 1 0%", auto: "1 1 auto", initial: "0 1 auto", none: "none" },
  flexGrow: { 0: "0", DEFAULT: "1" },
  flexShrink: { 0: "0", DEFAULT: "1" },
  gap: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
  },
  gradientColorStops: {
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
  },
  gridAutoColumns: {
    auto: "auto",
    min: "min-content",
    max: "max-content",
    fr: "minmax(0, 1fr)",
  },
  gridAutoRows: {
    auto: "auto",
    min: "min-content",
    max: "max-content",
    fr: "minmax(0, 1fr)",
  },
  gridColumn: {
    auto: "auto",
    "span-1": "span 1 / span 1",
    "span-2": "span 2 / span 2",
    "span-3": "span 3 / span 3",
    "span-4": "span 4 / span 4",
    "span-5": "span 5 / span 5",
    "span-6": "span 6 / span 6",
    "span-7": "span 7 / span 7",
    "span-8": "span 8 / span 8",
    "span-9": "span 9 / span 9",
    "span-10": "span 10 / span 10",
    "span-11": "span 11 / span 11",
    "span-12": "span 12 / span 12",
    "span-full": "1 / -1",
  },
  gridColumnEnd: {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    8: "8",
    9: "9",
    10: "10",
    11: "11",
    12: "12",
    13: "13",
    auto: "auto",
  },
  gridColumnStart: {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    8: "8",
    9: "9",
    10: "10",
    11: "11",
    12: "12",
    13: "13",
    auto: "auto",
  },
  gridRow: {
    auto: "auto",
    "span-1": "span 1 / span 1",
    "span-2": "span 2 / span 2",
    "span-3": "span 3 / span 3",
    "span-4": "span 4 / span 4",
    "span-5": "span 5 / span 5",
    "span-6": "span 6 / span 6",
    "span-full": "1 / -1",
  },
  gridRowStart: {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    auto: "auto",
  },
  gridRowEnd: {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    auto: "auto",
  },
  gridTemplateColumns: {
    1: "repeat(1, minmax(0, 1fr))",
    2: "repeat(2, minmax(0, 1fr))",
    3: "repeat(3, minmax(0, 1fr))",
    4: "repeat(4, minmax(0, 1fr))",
    5: "repeat(5, minmax(0, 1fr))",
    6: "repeat(6, minmax(0, 1fr))",
    7: "repeat(7, minmax(0, 1fr))",
    8: "repeat(8, minmax(0, 1fr))",
    9: "repeat(9, minmax(0, 1fr))",
    10: "repeat(10, minmax(0, 1fr))",
    11: "repeat(11, minmax(0, 1fr))",
    12: "repeat(12, minmax(0, 1fr))",
    none: "none",
  },
  gridTemplateRows: {
    1: "repeat(1, minmax(0, 1fr))",
    2: "repeat(2, minmax(0, 1fr))",
    3: "repeat(3, minmax(0, 1fr))",
    4: "repeat(4, minmax(0, 1fr))",
    5: "repeat(5, minmax(0, 1fr))",
    6: "repeat(6, minmax(0, 1fr))",
    none: "none",
  },
  height: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
    auto: "auto",
    "1/2": "50%",
    "1/3": "33.333333%",
    "2/3": "66.666667%",
    "1/4": "25%",
    "2/4": "50%",
    "3/4": "75%",
    "1/5": "20%",
    "2/5": "40%",
    "3/5": "60%",
    "4/5": "80%",
    "1/6": "16.666667%",
    "2/6": "33.333333%",
    "3/6": "50%",
    "4/6": "66.666667%",
    "5/6": "83.333333%",
    full: "100%",
    screen: "100vh",
  },
  inset: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
    auto: "auto",
    "-1": "-0.3rem",
    "-2": "-0.6rem",
    "-3": "-1.2rem",
    "-4": "-1.8rem",
    "-5": "-2.4rem",
    "-6": "-3rem",
    "-7": "-3.6rem",
    "-8": "-4.2rem",
    "-9": "-5.4rem",
    "-10": "-7.2rem",
    "1/2": "50%",
    "1/3": "33.333333%",
    "2/3": "66.666667%",
    "1/4": "25%",
    "2/4": "50%",
    "3/4": "75%",
    full: "100%",
    "-1/2": "-50%",
    "-1/3": "-33.333333%",
    "-2/3": "-66.666667%",
    "-1/4": "-25%",
    "-2/4": "-50%",
    "-3/4": "-75%",
    "-full": "-100%",
  },
  keyframes: {
    spin: { to: { transform: "rotate(360deg)" } },
    ping: { "75%, 100%": { transform: "scale(2)", opacity: "0" } },
    pulse: { "50%": { opacity: ".5" } },
    bounce: {
      "0%, 100%": {
        transform: "translateY(-25%)",
        animationTimingFunction: "cubic-bezier(0.8,0,1,1)",
      },
      "50%": {
        transform: "none",
        animationTimingFunction: "cubic-bezier(0,0,0.2,1)",
      },
    },
  },
  letterSpacing: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em",
  },
  lineHeight: {
    3: ".75rem",
    4: "1rem",
    5: "1.25rem",
    6: "1.5rem",
    7: "1.75rem",
    8: "2rem",
    9: "2.25rem",
    10: "2.5rem",
    none: "1",
    tight: "1.25",
    snug: "1.375",
    normal: "1.5",
    relaxed: "1.625",
    loose: "2",
  },
  margin: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
    auto: "auto",
    "-1": "-0.3rem",
    "-2": "-0.6rem",
    "-3": "-1.2rem",
    "-4": "-1.8rem",
    "-5": "-2.4rem",
    "-6": "-3rem",
    "-7": "-3.6rem",
    "-8": "-4.2rem",
    "-9": "-5.4rem",
    "-10": "-7.2rem",
  },
  maxHeight: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
    full: "100%",
    screen: "100vh",
  },
  maxWidth: {
    0: "0rem",
    none: "none",
    xs: "20rem",
    sm: "24rem",
    md: "28rem",
    lg: "32rem",
    xl: "36rem",
    "2xl": "42rem",
    "3xl": "48rem",
    "4xl": "56rem",
    "5xl": "64rem",
    "6xl": "72rem",
    "7xl": "80rem",
    full: "100%",
    min: "min-content",
    max: "max-content",
    prose: "65ch",
    "screen-sm": "640px",
    "screen-md": "768px",
    "screen-lg": "1024px",
    "screen-xl": "1280px",
    "screen-2xl": "1536px",
  },
  minHeight: { 0: "0px", full: "100%", screen: "100vh" },
  minWidth: { 0: "0px", full: "100%", min: "min-content", max: "max-content" },
  objectPosition: {
    bottom: "bottom",
    center: "center",
    left: "left",
    "left-bottom": "left bottom",
    "left-top": "left top",
    right: "right",
    "right-bottom": "right bottom",
    "right-top": "right top",
    top: "top",
  },
  opacity: {
    0: "0",
    5: "0.05",
    10: "0.1",
    20: "0.2",
    25: "0.25",
    30: "0.3",
    40: "0.4",
    50: "0.5",
    60: "0.6",
    70: "0.7",
    75: "0.75",
    80: "0.8",
    90: "0.9",
    95: "0.95",
    100: "1",
  },
  order: {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    8: "8",
    9: "9",
    10: "10",
    11: "11",
    12: "12",
    first: "-9999",
    last: "9999",
    none: "0",
  },
  outline: {
    none: ["2px solid transparent", "2px"],
    white: ["2px dotted white", "2px"],
    black: ["2px dotted black", "2px"],
  },
  padding: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
  },
  placeholderColor: {
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
  },
  placeholderOpacity: {
    0: "0",
    5: "0.05",
    10: "0.1",
    20: "0.2",
    25: "0.25",
    30: "0.3",
    40: "0.4",
    50: "0.5",
    60: "0.6",
    70: "0.7",
    75: "0.75",
    80: "0.8",
    90: "0.9",
    95: "0.95",
    100: "1",
  },
  ringColor: {
    DEFAULT: "#3b82f6",
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
  },
  ringOffsetColor: {
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
  },
  ringOffsetWidth: { 0: "0px", 1: "1px", 2: "2px", 4: "4px", 8: "8px" },
  ringOpacity: {
    0: "0",
    5: "0.05",
    10: "0.1",
    20: "0.2",
    25: "0.25",
    30: "0.3",
    40: "0.4",
    50: "0.5",
    60: "0.6",
    70: "0.7",
    75: "0.75",
    80: "0.8",
    90: "0.9",
    95: "0.95",
    100: "1",
    DEFAULT: "0.5",
  },
  ringWidth: {
    0: "0px",
    1: "1px",
    2: "2px",
    4: "4px",
    8: "8px",
    DEFAULT: "3px",
  },
  rotate: {
    0: "0deg",
    1: "1deg",
    2: "2deg",
    3: "3deg",
    6: "6deg",
    12: "12deg",
    45: "45deg",
    90: "90deg",
    180: "180deg",
    "-180": "-180deg",
    "-90": "-90deg",
    "-45": "-45deg",
    "-12": "-12deg",
    "-6": "-6deg",
    "-3": "-3deg",
    "-2": "-2deg",
    "-1": "-1deg",
  },
  scale: {
    0: "0",
    50: ".5",
    75: ".75",
    90: ".9",
    95: ".95",
    100: "1",
    105: "1.05",
    110: "1.1",
    125: "1.25",
    150: "1.5",
  },
  skew: {
    0: "0deg",
    1: "1deg",
    2: "2deg",
    3: "3deg",
    6: "6deg",
    12: "12deg",
    "-12": "-12deg",
    "-6": "-6deg",
    "-3": "-3deg",
    "-2": "-2deg",
    "-1": "-1deg",
  },
  space: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
    "-1": "-0.3rem",
    "-2": "-0.6rem",
    "-3": "-1.2rem",
    "-4": "-1.8rem",
    "-5": "-2.4rem",
    "-6": "-3rem",
    "-7": "-3.6rem",
    "-8": "-4.2rem",
    "-9": "-5.4rem",
    "-10": "-7.2rem",
  },
  stroke: { current: "currentColor" },
  strokeWidth: { 0: "0", 1: "1", 2: "2" },
  textColor: {
    primary: { DEFAULT: "#4c5e0d", light: "#728D13", dark: "#262F06" },
    secondary: { DEFAULT: "#d2d756", light: "#DFE387", dark: "#BDC32E" },
    tertiary: { DEFAULT: "#8469c9", light: "#A996D9", dark: "#6141B4" },
    success: { DEFAULT: "#2cc98f", light: "#4ED8A5", dark: "#23A172" },
    danger: { DEFAULT: "#e31a36", light: "#EA465D", dark: "#B6152B" },
    warning: { DEFAULT: "#ffc107", light: "#FFCE3B", dark: "#D29D00" },
    white: "#ffffff",
    black: "#212121",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
    transparent: "#0000",
  },
  textOpacity: {
    0: "0",
    5: "0.05",
    10: "0.1",
    20: "0.2",
    25: "0.25",
    30: "0.3",
    40: "0.4",
    50: "0.5",
    60: "0.6",
    70: "0.7",
    75: "0.75",
    80: "0.8",
    90: "0.9",
    95: "0.95",
    100: "1",
  },
  transformOrigin: {
    center: "center",
    top: "top",
    "top-right": "top right",
    right: "right",
    "bottom-right": "bottom right",
    bottom: "bottom",
    "bottom-left": "bottom left",
    left: "left",
    "top-left": "top left",
  },
  transitionDelay: {
    75: "75ms",
    100: "100ms",
    150: "150ms",
    200: "200ms",
    300: "300ms",
    500: "500ms",
    700: "700ms",
    1000: "1000ms",
  },
  transitionDuration: {
    75: "75ms",
    100: "100ms",
    150: "150ms",
    200: "200ms",
    300: "300ms",
    500: "500ms",
    700: "700ms",
    1000: "1000ms",
    DEFAULT: "150ms",
  },
  transitionProperty: {
    none: "none",
    all: "all",
    DEFAULT:
      "background-color, border-color, color, fill, stroke, opacity, box-shadow, transform",
    colors: "background-color, border-color, color, fill, stroke",
    opacity: "opacity",
    shadow: "box-shadow",
    transform: "transform",
  },
  transitionTimingFunction: {
    DEFAULT: "cubic-bezier(0.4, 0, 0.2, 1)",
    linear: "linear",
    in: "cubic-bezier(0.4, 0, 1, 1)",
    out: "cubic-bezier(0, 0, 0.2, 1)",
    "in-out": "cubic-bezier(0.4, 0, 0.2, 1)",
  },
  translate: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
    "-1": "-0.3rem",
    "-2": "-0.6rem",
    "-3": "-1.2rem",
    "-4": "-1.8rem",
    "-5": "-2.4rem",
    "-6": "-3rem",
    "-7": "-3.6rem",
    "-8": "-4.2rem",
    "-9": "-5.4rem",
    "-10": "-7.2rem",
    "1/2": "50%",
    "1/3": "33.333333%",
    "2/3": "66.666667%",
    "1/4": "25%",
    "2/4": "50%",
    "3/4": "75%",
    full: "100%",
    "-1/2": "-50%",
    "-1/3": "-33.333333%",
    "-2/3": "-66.666667%",
    "-1/4": "-25%",
    "-2/4": "-50%",
    "-3/4": "-75%",
    "-full": "-100%",
    "7/10": "70%",
    "-7/10": "-70%",
  },
  width: {
    0: "0",
    1: "0.3rem",
    2: "0.6rem",
    3: "1.2rem",
    4: "1.8rem",
    5: "2.4rem",
    6: "3rem",
    7: "3.6rem",
    8: "4.2rem",
    9: "5.4rem",
    10: "7.2rem",
    auto: "auto",
    "1/2": "50%",
    "1/3": "33.333333%",
    "2/3": "66.666667%",
    "1/4": "25%",
    "2/4": "50%",
    "3/4": "75%",
    "1/5": "20%",
    "2/5": "40%",
    "3/5": "60%",
    "4/5": "80%",
    "1/6": "16.666667%",
    "2/6": "33.333333%",
    "3/6": "50%",
    "4/6": "66.666667%",
    "5/6": "83.333333%",
    "1/12": "8.333333%",
    "2/12": "16.666667%",
    "3/12": "25%",
    "4/12": "33.333333%",
    "5/12": "41.666667%",
    "6/12": "50%",
    "7/12": "58.333333%",
    "8/12": "66.666667%",
    "9/12": "75%",
    "10/12": "83.333333%",
    "11/12": "91.666667%",
    full: "100%",
    screen: "100vw",
    min: "min-content",
    max: "max-content",
  },
  zIndex: {
    0: "0",
    10: "10",
    20: "20",
    30: "30",
    40: "40",
    50: "50",
    auto: "auto",
  },
};

export default theme;
